import { useEffect, useRef } from "react";
import { playSfx } from "../sound/sfx";

/**
 * Shared table sound hooks — every game table wires its cues through these, so
 * the behavior cannot drift per game.
 *
 * THE ASSUMPTION THAT WAS WRONG: these used to watch the trick's LENGTH and fire
 * on growth. But the engines CLEAR `currentTrick` on the trick's last card and
 * move the four plays into `completedTrick` (a display copy, held until the next
 * lead) — so from the client's side the trick goes 3 → 0 cards: a SHRINK. The
 * LAST CARD OF EVERY TRICK therefore made no sound (Hokm, Hearts and Shelem;
 * offline and online alike, since the tables are shared). Everything here is now
 * IDENTITY-based: a cue fires when the thing on the felt becomes a NEW thing.
 *
 * All of it is client-side decoration: `playSfx` no-ops when muted and never
 * throws, so these hooks are inert in tests and on broken audio stacks.
 */

export interface TrickPlayLike {
  readonly seat: number;
  readonly card: string;
}

export interface TrickViewLike {
  readonly seats: number;
  readonly you: number;
  /** Whoever leads next — after a trick completes this IS the trick winner. */
  readonly turn: number;
  readonly currentTrick: readonly TrickPlayLike[];
  readonly completedTrick?: readonly TrickPlayLike[];
  readonly playLog?: readonly unknown[];
}

/** The trick actually SHOWN on the felt: the live one, or (once the engine has
 *  cleared it on the last card) the completed copy it holds until the next lead. */
export function shownTrick(view: TrickViewLike): readonly TrickPlayLike[] {
  return view.currentTrick.length > 0 ? view.currentTrick : (view.completedTrick ?? []);
}

/** A card (or claim) that just landed, with a stable identity. */
export interface LandedPlay {
  readonly seat: number;
  readonly key: string;
}

/** The last card on the felt — the ONE thing whose identity drives the play cue.
 *  Cards are unique within a hand, and a completed trick carried into the next
 *  hand (the engines do that so its sweep can finish) keeps its key, so it can
 *  never re-trigger. */
export function lastPlayOf(view: TrickViewLike): LandedPlay | null {
  const trick = shownTrick(view);
  const last = trick[trick.length - 1];
  return last ? { seat: last.seat, key: `${trick.length}:${last.seat}:${last.card}` } : null;
}

/** True when nothing has been played yet — i.e. what is on screen IS a fresh deal
 *  (as opposed to a mid-hand resume, which must never play a phantom shuffle). */
export function isVirginDeal(view: {
  readonly currentTrick?: readonly unknown[];
  readonly completedTrick?: readonly unknown[];
  readonly playLog?: readonly unknown[];
}): boolean {
  return (
    (view.playLog?.length ?? 0) === 0 &&
    (view.currentTrick?.length ?? 0) === 0 &&
    (view.completedTrick?.length ?? 0) === 0
  );
}

/**
 * The DEAL cue («shuffle»). Fires when cards are actually dealt:
 *  - at MOUNT only when the table is VIRGIN (a fresh deal is on screen) — so
 *    resuming a saved game mid-hand no longer plays a phantom shuffle;
 *  - afterwards whenever the hand GROWS by more than one card (a new hand, the
 *    hakem's opening 5, a Pasur refill) — never a single-card pickup.
 *
 * `onGrowth: false` suppresses the growth path (Bluff: taking the pile grows the
 * hand by many cards but is NOT a deal).
 */
export function useDealSfx(
  handSize: number,
  virgin: boolean,
  opts: { onGrowth?: boolean } = {},
): void {
  const onGrowth = opts.onGrowth ?? true;
  const prev = useRef<number | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = handSize;
    if (before === null) {
      if (virgin && handSize > 0) playSfx("shuffle"); // a fresh deal is on screen
      return;
    }
    if (onGrowth && handSize > before + 1) playSfx("shuffle");
  }, [handSize, virgin, onGrowth]);
}

/**
 * The CARD-PLAYED cue for cards played by ANOTHER seat (your own tap already
 * clicks inside `Card`). Fires whenever the last thing on the felt becomes a NEW
 * one — including the trick's LAST card, which the old growth check missed.
 * Adopts the current value at mount, so a resume never replays the last card.
 */
export function useCardLandSfx(last: LandedPlay | null, you: number): void {
  const prev = useRef<string | null>(last?.key ?? null);
  const key = last?.key ?? null;
  const seat = last?.seat ?? null;
  useEffect(() => {
    if (key === prev.current) return;
    prev.current = key;
    if (key !== null && seat !== null && seat !== you) playSfx("play");
  }, [key, seat, you]);
}

/**
 * The TRICK-RESULT cue: chips clink toward you on a trick you won, cards shove
 * away on one you lost — `delayMs` after the trick completes, so it lands with
 * the collect sweep instead of on top of the last card's own sound. Once per
 * trick (identity-keyed), and silent for a trick already complete at mount.
 */
export function useTrickResultSfx(view: TrickViewLike, delayMs: number): void {
  const completed =
    view.currentTrick.length === 0 && (view.completedTrick?.length ?? 0) === view.seats
      ? (view.completedTrick ?? [])
      : [];
  const sig = completed.map((p) => `${p.seat}:${p.card}`).join(",");
  const wonByYou = view.turn === view.you; // the winner leads next
  const prev = useRef<string | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = sig;
    if (before === null) return undefined; // mount (maybe a resume): adopt, never fire
    if (!sig || sig === before) return undefined;
    const timer = setTimeout(() => playSfx(wonByYou ? "trickWin" : "trickLose"), delayMs);
    return () => clearTimeout(timer);
  }, [sig, wonByYou, delayMs]);
}

/**
 * A one-shot cue keyed by `sig`: fires whenever `sig` changes to a NEW non-empty
 * value (never at mount). Powers the per-game moments where an event, not a
 * count, is the trigger — a Pasur capture, a Bluff challenge resolution.
 */
export function useEventSfx(sig: string, sound: Parameters<typeof playSfx>[0]): void {
  const prev = useRef<string | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = sig;
    if (before === null || !sig || sig === before) return;
    playSfx(sound);
  }, [sig, sound]);
}

/**
 * SOMEBODY ARRIVED, OR SOMEBODY WALKED OUT — the waiting room says so out loud.
 *
 * `othersKey` is a stable key for EVERYONE AT THE TABLE EXCEPT YOU (identities, not
 * chairs). Two consequences, both deliberate:
 *
 *  - It can never fire at the person who caused it. Your own arrival is not news to
 *    you — you are the one who tapped join — and this was the complaint: the joiner
 *    heard the notification meant for the people already sitting there.
 *  - Re-seating the same people (the host arranging teams swaps two chairs) is NOT a
 *    departure. Keying on seats would have called every swap "someone left".
 *
 * `null` ⇒ we do not know our own seat yet, so we cannot tell who "everyone else" is:
 * stay silent rather than sound a phantom arrival when the seat frame lands.
 */
export function useRosterSfx(othersKey: string | null): void {
  const prev = useRef<string | null>(null);
  useEffect(() => {
    if (othersKey === null) return; // seat unknown — nothing to compare against
    const before = prev.current;
    prev.current = othersKey;
    if (before === null || before === othersKey) return; // mount: adopt in silence
    playSfx("notify");
  }, [othersKey]);
}
